import React, { useRef, useState, useEffect } from "react";
import type { TableColumnsType, TableProps } from "antd";
import {
  Button,
  Flex,
  Input,
  message,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Typography,
} from "antd";
import { DeleteFilled, EyeFilled, SearchOutlined } from "@ant-design/icons";
import type { InputRef } from "antd";
import type { FilterDropdownProps } from "antd/es/table/interface";
import { useMutation } from "@tanstack/react-query";
import {
  getAdverts,
  updateAdvertStatus,
  deleteAdvert,
} from "../../services/advertService";
import AdvertResponse from "../../payload/response/AdvertResponse";
import { useNavigate } from "react-router-dom";
import { onError } from "../../helper/helper";
import SelectManufacturer from "../../components/SelectManufacturer/SelectManufacturer";
import SelectCategory from "../../components/SelectCategory/SelectCategory";
import ProductImage from "../../components/ProtectedImage/ProtectedImage";

type OnChange = NonNullable<TableProps<DataType>["onChange"]>;
type Filters = Parameters<OnChange>[1];

type GetSingle<T> = T extends (infer U)[] ? U : never;
type Sorts = GetSingle<Parameters<OnChange>[2]>;

interface DataType extends AdvertResponse { }

const { Title } = Typography;

const Adverts: React.FC = () => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState<string>("");
  const [searchedColumn, setSearchedColumn] = useState<string>("");
  const searchInput = useRef<InputRef>(null);

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });

  const [filters, setFilters] = useState({
    productId: undefined as number | undefined,
    manufacturerId: undefined as number | undefined,
    categoryId: undefined as number | undefined,
    isActive: undefined as boolean | undefined,
  });

  const [selectedManufacturerId, setSelectedManufacturerId] = useState<number | undefined>(undefined);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | undefined>(undefined);



  const [sort, setSort] = useState<{ sortField?: string; sortDirection?: string }>({
    sortField: undefined,
    sortDirection: undefined,
  });

  const [advertsData, setAdvertsData] = useState<any>(null);

  const { mutate: fetchAdverts, isPending: isLoading } = useMutation({
    mutationFn: () =>
      getAdverts(pagination.current - 1, pagination.pageSize, {
        productId: filters.productId,
        manufacturerId: filters.manufacturerId,
        categoryId: filters.categoryId,
        isActive: filters.isActive,
        sortField: sort.sortField,
        sortDirection: sort.sortDirection,
      }),
    onSuccess: (data) => {
      setAdvertsData(data);
      if (data?.totalPage) {
        setPagination((prev) => ({
          ...prev,
          total: data.totalPage * pagination.pageSize,
        }));
      }
    },
    onError: (error: any) => {
      onError(error);
    },
  });

  useEffect(() => {
    fetchAdverts();
  }, [
    pagination.current,
    pagination.pageSize,
    filters.productId,
    filters.manufacturerId,
    filters.categoryId,
    filters.isActive,
    sort.sortField,
    sort.sortDirection,
  ]);

  const data = advertsData;

  const [filteredInfo, setFilteredInfo] = useState<Filters>({});
  const [sortedInfo, setSortedInfo] = useState<Sorts>({});

  const handleChange: OnChange = (pagination, filters, sorter) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter as Sorts);
  };

  const { mutate: mutateStatus } = useMutation({
    mutationFn: (id: number) => updateAdvertStatus(id),
    onSuccess: () => {
      message.success("İlan durumu güncellendi");
      fetchAdverts();
    },
    onError(error, variables, context) {
      message.error("Bir hata oluştu");
    },
  });

  const { mutate: mutateDelete } = useMutation({
    mutationFn: (id: number) => deleteAdvert(id),
    onSuccess: () => {
      message.success("İlan silindi");
      fetchAdverts();
    },
    onError(error: any) {
      onError(error);
    },
  });

  const getColumnSearchProps = (dataIndex: keyof DataType): any => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }: FilterDropdownProps) => (
      <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
        <Input
          ref={searchInput}
          placeholder="Ara"
          value={selectedKeys[0] as string}
          onChange={(e) => {
            const val = e.target.value;
            setSelectedKeys(val ? [val] : []);
            confirm({ closeDropdown: false });
          }}
          onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Ara
          </Button>
          <Button
            onClick={() => handleReset(clearFilters, confirm)}
            size="small"
            style={{ width: 90 }}
          >
            Temizle
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value: string | number | boolean, record: DataType) => {
      const recordValue = (record[dataIndex] ?? "") as unknown as string;
      return recordValue?.toString()?.toLowerCase()?.includes((value as string).toLowerCase());
    },
    onFilterDropdownOpenChange: (visible: boolean) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
  });

  const handleSearch = (
    selectedKeys: string[],
    confirm: (param?: any) => void,
    dataIndex: keyof DataType
  ) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex as string);
  };

  const handleReset = (clearFilters?: () => void, confirm?: (param?: any) => void) => {
    clearFilters?.();
    setSearchText("");
    confirm?.({ closeDropdown: false });
  };


  const columns: TableColumnsType<DataType> = [
    {
      title: "Ürün Resmi",
      dataIndex: "product",
      key: "productImage",
      width: 100,
      render: (_, record) => {
        return (
          <ProductImage
            imageUrl={record?.product?.images?.[0]?.imageUrl || "error"}
            name={record.product?.name || ""}
          />
        );
      },
    },
    {
      title: "Ürün Adı",
      dataIndex: "product",
      key: "productName",
      sorter: (a, b) => (a.product?.name || "").localeCompare(b.product?.name || ""),
      sortOrder: sortedInfo.columnKey === "productName" ? sortedInfo.order : null,
      ellipsis: true,
      render: (_, record) => <span>{record.product?.name}</span>,
    },
    {
      title: "Bayi Adı",
      dataIndex: "dealer",
      key: "dealerName",
      ellipsis: true,
      render: (_, record) => <span>{record.dealer?.fullName}</span>,
    },
    {
      title: "Firma Adı",
      dataIndex: "dealer",
      key: "companyName",
      ellipsis: true,
      render: (_, record) => <span>{record.dealer?.companyName}</span>,
    },
    {
      title: "Üretici Firma",
      dataIndex: "product",
      key: "manufacturerName",
      ellipsis: true,
      render: (_, record) => <span>{record.product?.manufacturer?.name}</span>,
    },
    {
      title: "Kategori",
      dataIndex: "product",
      key: "categoryName",
      ellipsis: true,
      render: (_, record) => <span>{record.product?.categoryName}</span>,
    },
    {
      title: "Başlangıç Tarihi",
      dataIndex: "startDate",
      key: "startDate",
      sorter: (a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime(),
      sortOrder: sortedInfo.columnKey === "startDate" ? sortedInfo.order : null,
      render: (value) => <span>{new Date(value).toLocaleDateString("tr-TR")}</span>,
    },
    {
      title: "Bitiş Tarihi",
      dataIndex: "expiryDate",
      key: "expiryDate",
      sorter: (a, b) => new Date(a.expiryDate).getTime() - new Date(b.expiryDate).getTime(),
      sortOrder: sortedInfo.columnKey === "expiryDate" ? sortedInfo.order : null,
      render: (value) => <span>{new Date(value).toLocaleDateString("tr-TR")}</span>,
    },
    {
      title: "Durum",
      dataIndex: "isActive",
      key: "isActive",
      render: (text, record) => {
        return (
          <Space key={record.id}>
            <Switch
              checkedChildren="Aktif"
              unCheckedChildren="Pasif"
              onChange={(value) => {
                mutateStatus(record.id);
              }}
              checked={record.isActive}
            />
          </Space>
        );
      },
    },
    {
      title: "İşlem",
      key: "action",
      render: (_, record) => (
        <Space key={record.id} size="middle">
          <Popconfirm
            placement="bottomLeft"
            title="Silmek istediğinize emin misiniz?"
            description="Bu işlem geri alınamaz ve tüm veriler silinecektir."
            onConfirm={() => {
              mutateDelete(record.id);
            }}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button type="primary" danger>
              <DeleteFilled />
            </Button>
          </Popconfirm>
          <Button
            onClick={() => {
              navigate(`/admin/dealer/${record.dealer?.id}`);
            }}
            type="primary"
          >
            <EyeFilled />
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <Title level={2}>İlanlar</Title>
      </Flex>
      <Space size={8} style={{ marginBottom: 16, width: "100%" }} wrap>
        <SelectManufacturer
          value={selectedManufacturerId}
          onChange={(value) => {
            setPagination((prev) => ({ ...prev, current: 1 }));
            setSelectedManufacturerId(value);
            setFilters((prev) => ({
              ...prev,
              manufacturerId: value,
            }));
          }}
          style={{ width: 400 }}
        />
        <SelectCategory
          value={selectedCategoryId}
          onChange={(value) => {
            setPagination((prev) => ({ ...prev, current: 1 }));
            setSelectedCategoryId(value);
            setFilters((prev) => ({
              ...prev,
              categoryId: value,
            }));
          }}
          showRootCategory={false}
          isOnlySelectSub={true}
          style={{ width: 400 }}
        />
        <Select
          placeholder="Durum"
          allowClear
          style={{ width: 280 }}
          value={filters.isActive !== undefined ? String(filters.isActive) : undefined}
          onChange={(val) => {
            setPagination((prev) => ({ ...prev, current: 1 }));
            setFilters((prev) => ({
              ...prev,
              isActive: val !== undefined ? val === "true" : undefined,
            }));
          }}
          options={[
            { label: "Aktif", value: "true" },
            { label: "Pasif", value: "false" },
          ]}
        />
        <Select
          placeholder="Sırala"
          allowClear
          style={{ width: 280 }}
          value={sort.sortField && sort.sortDirection ? `${sort.sortField}_${sort.sortDirection}` : undefined}
          onChange={(val) => {
            setPagination((prev) => ({ ...prev, current: 1 }));
            if (!val) {
              setSort({ sortField: undefined, sortDirection: undefined });
              return;
            }
            const [field, direction] = val.split("_");
            setSort({ sortField: field, sortDirection: direction });
          }}
          options={[
            { label: "Ürün Adı A-Z", value: "productName_asc" },
            { label: "Ürün Adı Z-A", value: "productName_desc" },
          ]}
        />
        <Button
          onClick={() => {
            setFilters({
              productId: undefined,
              manufacturerId: undefined,
              categoryId: undefined,
              isActive: undefined,
            });
            setSelectedManufacturerId(undefined);
            setSelectedCategoryId(undefined);
            setSort({ sortField: undefined, sortDirection: undefined });
            setPagination((prev) => ({ ...prev, current: 1 }));
          }}
        >
          Temizle
        </Button>
      </Space>
      <Table<DataType>
        loading={isLoading}
        columns={columns}
        dataSource={data?.list?.map((advert: AdvertResponse) => ({ ...advert, key: advert.id })) || []}
        onChange={handleChange}
        scroll={{ x: 1500 }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          onChange: (page, pageSize) => {
            setPagination((prev) => ({
              ...prev,
              current: page,
              pageSize: pageSize,
            }));
          },
        }}
      />
      {data?.count !== undefined && (
        <Typography.Text style={{ marginTop: 16, display: "block" }}>
          Toplam bulunan kayıt sayısı: <strong>{data.count}</strong>
        </Typography.Text>
      )}
    </>
  );
};

export default Adverts;
